import L from "leaflet";
import type { MapObject } from "@fortex/api";
import { numericId } from "../core/support";
import type { SnapCandidate } from "../geo/snap";
import { geometryPoints, geometrySegments } from "../geo/wkt";
import type { WktGeometry } from "../geo/wkt";
import { mapObjectFromFeature } from "./adapters";
import type { Feature, LatLngTuple } from "./types";

export type Projector = { latLngToLayerPoint(latlng: L.LatLngExpression): L.Point };

export function featureObjectId(feature: Feature): number | null {
  if (feature.cluster) return null;

  return feature.object?.id ?? numericId(feature.id);
}

function pointInRing(point: LatLngTuple, ring: LatLngTuple[]): boolean {
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const crosses = a[1] > point[1] !== b[1] > point[1] && point[0] < ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0];

    if (crosses) inside = !inside;
  }

  return inside;
}

export function geometryContains(geometry: WktGeometry, point: LatLngTuple): boolean {
  switch (geometry.kind) {
    case "polygon":
      return geometry.rings.length > 0 && pointInRing(point, geometry.rings[0]) && !geometry.rings.slice(1).some(ring => pointInRing(point, ring));
    case "multi":
      return geometry.parts.some(part => geometryContains(part, point));
    default:
      return false;
  }
}

function segmentDistancePx(from: L.Point, to: L.Point, target: L.Point): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  const fraction = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((target.x - from.x) * dx + (target.y - from.y) * dy) / lengthSquared));

  return target.distanceTo(L.point(from.x + dx * fraction, from.y + dy * fraction));
}

export function geometryDistancePx(geometry: WktGeometry, latlng: LatLngTuple, projector: Projector): number {
  const target = projector.latLngToLayerPoint(latlng);

  switch (geometry.kind) {
    case "point":
      return target.distanceTo(projector.latLngToLayerPoint(geometry.point));
    case "line": {
      let best = Number.POSITIVE_INFINITY;

      for (const [from, to] of geometrySegments(geometry)) best = Math.min(best, segmentDistancePx(projector.latLngToLayerPoint(from), projector.latLngToLayerPoint(to), target));

      return best;
    }
    case "polygon": {
      if (geometryContains(geometry, latlng)) return 0;

      let best = Number.POSITIVE_INFINITY;

      for (const [from, to] of geometrySegments(geometry)) best = Math.min(best, segmentDistancePx(projector.latLngToLayerPoint(from), projector.latLngToLayerPoint(to), target));

      return best;
    }
    case "multi":
      return Math.min(...geometry.parts.map(part => geometryDistancePx(part, latlng, projector)));
  }
}

export function geometryBounds(geometry: WktGeometry): L.LatLngBounds | null {
  const points = geometryPoints(geometry);

  if (points.length === 0) return null;

  const bounds = L.latLngBounds(points);

  return bounds.isValid() ? bounds : null;
}

export class FeatureIndex {
  private readonly byLayer = new Map<string, Feature[]>();
  private readonly keys = new Map<string, Feature>();
  private readonly objectIds = new Map<number, Feature>();

  set(layerId: string, features: Feature[]): void {
    this.remove(layerId);
    this.byLayer.set(layerId, [...features]);

    for (const feature of features) {
      this.keys.set(feature.key, feature);

      const objectId = featureObjectId(feature);

      if (objectId !== null && !this.objectIds.has(objectId)) this.objectIds.set(objectId, feature);
    }
  }

  remove(layerId: string): void {
    const existing = this.byLayer.get(layerId);

    if (existing === undefined) return;

    for (const feature of existing) {
      this.keys.delete(feature.key);

      const objectId = featureObjectId(feature);

      if (objectId !== null && this.objectIds.get(objectId) === feature) this.objectIds.delete(objectId);
    }

    this.byLayer.delete(layerId);
  }

  clear(): void {
    this.byLayer.clear();
    this.keys.clear();
    this.objectIds.clear();
  }

  layers(): string[] {
    return [...this.byLayer.keys()];
  }

  forLayer(layerId: string): Feature[] {
    return this.byLayer.get(layerId) ?? [];
  }

  all(): Feature[] {
    return [...this.byLayer.values()].flat();
  }

  byKey(key: string): Feature | null {
    return this.keys.get(key) ?? null;
  }

  byObjectId(id: number): Feature | null {
    return this.objectIds.get(id) ?? null;
  }

  objects(): MapObject[] {
    const result: MapObject[] = [];

    for (const feature of this.all()) {
      const object = mapObjectFromFeature(feature);

      if (object !== null) result.push(object);
    }

    return result;
  }

  intersecting(bounds: L.LatLngBounds): Feature[] {
    const result: Feature[] = [];

    for (const feature of this.all()) {
      if (feature.cluster) continue;

      const points = geometryPoints(feature.geometry);
      const inside = points.length === 0 ? bounds.contains(feature.anchor) : points.some(point => bounds.contains(point));
      const extent = geometryBounds(feature.geometry);

      if (inside || (extent !== null && feature.geometry.kind !== "point" && extent.intersects(bounds))) result.push(feature);
    }

    return result;
  }

  snapCandidates(): SnapCandidate[] {
    const candidates: SnapCandidate[] = [];

    for (const feature of this.all()) {
      if (feature.cluster) continue;

      const id = featureObjectId(feature) ?? -2;

      for (const point of geometryPoints(feature.geometry)) candidates.push({ id, kind: "vertex", points: [point] });
      for (const segment of geometrySegments(feature.geometry)) candidates.push({ id, kind: "segment", points: segment });
    }

    return candidates;
  }

  nearest(latlng: LatLngTuple, projector: Projector, radiusPx: number, layerIds?: string[]): Feature | null {
    const hits = this.within(latlng, projector, radiusPx, layerIds);

    return hits.length > 0 ? hits[0] : null;
  }

  within(latlng: LatLngTuple, projector: Projector, radiusPx: number, layerIds?: string[]): Feature[] {
    const source = layerIds === undefined ? this.all() : layerIds.flatMap(id => this.forLayer(id));
    const scored: { feature: Feature; distance: number }[] = [];

    for (const feature of source) {
      const distance = geometryDistancePx(feature.geometry, latlng, projector);

      if (distance <= radiusPx) scored.push({ feature, distance });
    }

    scored.sort((a, b) => a.distance - b.distance || rank(a.feature) - rank(b.feature));

    return scored.map(a => a.feature);
  }
}

function rank(feature: Feature): number {
  switch (feature.geometry.kind) {
    case "point":
      return 0;
    case "line":
      return 1;
    case "polygon":
      return 2;
    case "multi":
      return 3;
  }
}
